/**
 * PersistentContextBar — sticky breadcrumb + save-status indicator
 * at the top of every authenticated screen (issue #433).
 *
 * Renders nothing on the caseload root (per acceptance criterion
 * #8) and falls back to nothing on mobile viewports — the bar is
 * a desktop-only affordance.
 *
 * The bar is sticky-positioned below the browser chrome (the
 * shell scrolls inside the viewport; the bar pins to the top of
 * the canvas).
 *
 * Breadcrumb links use the caller-supplied `navigateTo` so a
 * click on "Caseload" or "Student" reuses the same pushState path
 * the rest of the app uses (issue #472) — no full document
 * reload, no loss of in-memory state.
 */
import type { ReactElement } from 'react';
import { useBreakpoint } from '../lib/ui/useBreakpoint';

export interface BreadcrumbSegment {
  label: string;
  href?: string;
}

export type SaveIndicator = 'synced' | 'saving' | 'offline' | 'unsaved';

export interface PersistentContextBarProps {
  /** Trail from root to current; empty array hides the bar. */
  breadcrumb: BreadcrumbSegment[];
  /** SPA navigator (history.pushState). Required so breadcrumb
   *  clicks don't trigger full document reloads (issue #472). */
  navigateTo: (path: string) => void;
  /** Save state shown as a chip on the right. */
  save?: SaveIndicator;
  /** Optional custom save-state label, overrides the default copy. */
  saveLabel?: string;
}

function defaultSaveLabel(state: SaveIndicator): string {
  switch (state) {
    case 'synced':
      return 'Synced';
    case 'saving':
      return 'Saving…';
    case 'offline':
      return 'Offline';
    case 'unsaved':
      return 'Unsaved';
  }
}

export function PersistentContextBar({
  breadcrumb,
  navigateTo,
  save,
  saveLabel,
}: PersistentContextBarProps): ReactElement | null {
  const bp = useBreakpoint();

  if (bp !== 'desktop' && bp !== 'wide') return null;
  if (breadcrumb.length === 0) return null;

  return (
    <div className="context-bar" role="region" aria-label="Page context">
      <nav className="context-bar-breadcrumb" aria-label="Breadcrumb">
        {breadcrumb.map((seg, idx) => {
          const isLast = idx === breadcrumb.length - 1;
          return (
            <span key={`${idx}-${seg.label}`} className="context-bar-segment">
              {seg.href && !isLast ? (
                <a
                  href={seg.href}
                  className="context-bar-link"
                  onClick={(event) => {
                    // SPA-nav instead of full document reload
                    // (issue #472). Modifier-key + non-left clicks
                    // fall through to the browser so "open in new
                    // tab" still works.
                    if (
                      event.defaultPrevented
                      || event.button !== 0
                      || event.metaKey
                      || event.ctrlKey
                      || event.shiftKey
                      || event.altKey
                    ) {
                      return;
                    }
                    event.preventDefault();
                    navigateTo(seg.href as string);
                  }}
                >
                  {seg.label}
                </a>
              ) : (
                <span
                  className={
                    isLast
                      ? 'context-bar-current'
                      : 'context-bar-link context-bar-link-static'
                  }
                  aria-current={isLast ? 'page' : undefined}
                >
                  {seg.label}
                </span>
              )}
              {!isLast && (
                <span className="context-bar-sep" aria-hidden="true">
                  ›
                </span>
              )}
            </span>
          );
        })}
      </nav>

      {save !== undefined && (
        <span
          className={`context-bar-status context-bar-status--${save}`}
          aria-live="polite"
        >
          {saveLabel ?? defaultSaveLabel(save)}
        </span>
      )}
    </div>
  );
}
